export const authErrorKeys = {
  "auth.invalid_credentials": "cowork.login.error.invalidCredentials",
  "auth.account_exists": "cowork.login.error.accountExists",
  "auth.request_failed": "cowork.login.error.requestFailed",
  "auth.password_required": "cowork.login.error.passwordRequired",
  "auth.password_too_short": "cowork.login.error.passwordTooShort",
  "auth.password_too_long": "cowork.login.error.passwordTooLong",
  "auth.password_invalid": "cowork.login.error.passwordInvalid",
  "auth.invalid_email": "cowork.login.error.invalidEmail",
  "auth.invalid_full_name": "cowork.login.error.invalidFullName",
  "auth.invalid_organization": "cowork.login.error.invalidOrganization",
  "auth.network_error": "cowork.login.error.networkError",
  "auth.service_unavailable": "cowork.login.error.serviceUnavailable",
  "auth.email_not_verified": "cowork.login.error.emailNotVerified",
  "auth.invalid_link": "cowork.login.error.invalidLink",
  "auth.invitation_not_found": "cowork.login.error.invitationNotFound",
  "auth.invitation_gone": "cowork.login.error.invitationGone",
  "auth.invitation_wrong_account": "cowork.login.error.invitationWrongAccount",
} as const

export function authErrorKey(error: unknown): string {
  const status = typeof error === "object" && error && "status" in error ? Number(error.status) : 0
  const detail = error instanceof Error ? error.message : ""
  if (status === 401) return "auth.invalid_credentials"
  if (status === 404) return "auth.invitation_not_found"
  if (status === 410) return "auth.invitation_gone"
  if (status === 409) return "auth.account_exists"
  if (status === 403 && detail === "email_not_verified") return "auth.email_not_verified"
  if (status === 403) return "auth.invitation_wrong_account"
  if (status === 400) return "auth.invalid_link"
  if (Object.hasOwn(authErrorKeys, detail)) return detail
  return "auth.request_failed"
}

export function passwordValidationError(password: string) {
  // Match Pydantic's character count, including non-BMP Unicode characters.
  const length = [...password].length
  if (!length) return "auth.password_required"
  if (length < 8) return "auth.password_too_short"
  if (length > 256) return "auth.password_too_long"
  return undefined
}

export async function passwordAuthError(response: Response, mode: "signin" | "signup") {
  if (mode === "signin" && response.status === 401) return "auth.invalid_credentials"
  if (mode === "signup" && response.status === 409) return "auth.account_exists"
  if (response.status >= 500) return "auth.service_unavailable"
  try {
    const payload: unknown = await response.json()
    if (!payload || typeof payload !== "object" || !("detail" in payload)) return "auth.request_failed"
    if (response.status === 422 && Array.isArray(payload.detail)) {
      for (const error of payload.detail) {
        if (!error || typeof error !== "object" || !Array.isArray(error.loc) || error.loc[0] !== "body") continue
        // Only use field names and error types. Validation responses can echo passwords in `input`.
        switch (error.loc[1]) {
          case "password":
            if (error.type === "missing") return "auth.password_required"
            if (error.type === "string_too_short") return "auth.password_too_short"
            if (error.type === "string_too_long") return "auth.password_too_long"
            return "auth.password_invalid"
          case "email":
            return "auth.invalid_email"
          case "full_name":
            return "auth.invalid_full_name"
          case "organization":
            return "auth.invalid_organization"
        }
      }
    }
    if (payload.detail === "email_not_verified") return "auth.email_not_verified"
    if (typeof payload.detail === "string" && payload.detail.trim()) return payload.detail
  } catch {
    return "auth.request_failed"
  }
  return "auth.request_failed"
}
